import AuthContent from './AuthContent'
import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import { useEffect } from 'react'
import ModalDialog from '~/src/components/ModalDialog'
import { useAuthContext } from '~/src/context/AuthProvider'
import { useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import { useAuthWebToken } from '~/src/hooks/useAuthWebToken'
import useInviteCodeFromUrl from '~/src/hooks/useInviteCodeFromUrl'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import { RECAPTCA_ID } from '~/src/lib/constants'

const AuthModal: React.FC = () => {
  const analytics = useAnalytics()

  // Detect and store invite code from URL
  const { inviteCode: inviteCodeFromUrl, clearInviteCode } =
    useInviteCodeFromUrl()

  const { authUser } = useAuthUser()
  const { isAuthWebTokenActive, wasAuthWebTokenActive } = useAuthWebToken()
  const {
    isOpen,
    closeAuthModal,
    authSource,
    disableClose,
    fullScreen,
    blurOverlay,
    handleAuthSuccess,
    openAuthModal,
  } = useAuthContext()

  const { resetProcessing } = useSearchContext()

  const { featureOn: rewardsProgramFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.RewardsProgram
  )

  // If invite code is in url, open auth modal
  useEffect(() => {
    if (!rewardsProgramFeatureEnabled) return
    if (isOpen) return
    if (authUser) return

    if (inviteCodeFromUrl) {
      openAuthModal({
        source: 'invite-code',
        modalTitle: `Join your friend on Dupe.com`,
        modalSubtitle: `Invite code ${inviteCodeFromUrl}`,
        fullScreen: false,
        blurOverlay: true,
        onClose: () => {
          clearInviteCode()
        },
      })
    }
  }, [
    authUser,
    inviteCodeFromUrl,
    isOpen,
    openAuthModal,
    clearInviteCode,
    rewardsProgramFeatureEnabled,
  ])

  return (
    <>
      <div id={RECAPTCA_ID} className='flex items-center justify-center' />
      <ModalDialog
        show={isOpen}
        fullScreen={fullScreen}
        blurOverlay={blurOverlay}
        width={fullScreen ? undefined : 600}
        onClose={() => {
          closeAuthModal()
        }}
        showCloseButton={!disableClose}
        onCloseButtonClick={() => {
          // Handle modal close based on auth state:
          // 1. If user is not authenticated, reset processing state
          // 2. If user is authenticated but hasn't completed onboarding,
          //    execute callback to maintain authenticated state
          if (!authUser) {
            resetProcessing()
          } else if (
            handleAuthSuccess &&
            !isAuthWebTokenActive &&
            !wasAuthWebTokenActive
          ) {
            handleAuthSuccess()
          }
          analytics.track(AnalyticsEventName.AuthModalClosed, {
            source: authSource,
          })
        }}
        disableClickOutsideClose
        closeOnSwipeDown={false}
        customClassNames='flex flex-col justify-center'
      >
        {({ closeModal }) => <AuthContent closeCallback={closeModal} />}
      </ModalDialog>
    </>
  )
}

export default AuthModal
